'use client';

import { 
  BookOpen, 
  Users, 
  RotateCcw, 
  AlertTriangle,
  TrendingUp,
  Clock,
  BookMarked,
  Activity
} from 'lucide-react';
import { StatsCard } from '@/components/stats-card';
import { ActivityChart } from '@/components/activity-chart';
import { BorrowRecordCard } from '@/components/borrow-record-card';
import { Header } from '@/components/header';
import { dashboardStats, borrowRecords } from '@/lib/data';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Link from 'next/link';

export default function DashboardPage() {
  const recentBorrows = borrowRecords.slice(0, 5);
  const overdueRecords = borrowRecords.filter(r => r.status === 'overdue');

  return (
    <div className="min-h-screen bg-[#0A192F]">
      <Header 
        title="仪表盘" 
        subtitle="欢迎使用智图管理系统，以下是今日数据概览"
        showActions={false}
      />

      <div className="p-8 space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatsCard
            title="图书总量"
            value={dashboardStats.totalBooks.toLocaleString()}
            description="馆藏图书总数"
            icon={BookOpen}
            trend={5.2}
            trendLabel="较上月"
            variant="default"
          />
          <StatsCard
            title="注册用户"
            value={dashboardStats.totalUsers.toLocaleString()}
            description="系统注册用户总数"
            icon={Users}
            trend={8.1}
            trendLabel="较上月"
            variant="success"
          />
          <StatsCard
            title="当前借阅"
            value={dashboardStats.activeBorrows}
            description="正在借阅中的图书"
            icon={RotateCcw}
            trend={-2.3}
            trendLabel="较上周"
            variant="warning"
          />
          <StatsCard
            title="逾期图书"
            value={dashboardStats.overdueBooks}
            description="需要催收的图书"
            icon={AlertTriangle}
            trend={12.5}
            trendLabel="需关注"
            variant="danger"
          />
        </div>

        {/* Charts & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity Chart */}
          <Card className="lg:col-span-2 bg-[#112240] border-[#1d3557]">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-[#E6F1FF] flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#64FFDA]" />
                  借阅趋势
                </CardTitle>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#64FFDA]" />
                  <span className="text-[#8892B0]">借阅量</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#3B82F6]" />
                  <span className="text-[#8892B0]">归还量</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ActivityChart data={dashboardStats.monthlyTrend} />
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card className="bg-[#112240] border-[#1d3557]">
            <CardHeader>
              <CardTitle className="text-[#E6F1FF] flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#64FFDA]" />
                今日动态
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-[#0A192F]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#64FFDA]/20 flex items-center justify-center">
                    <BookMarked className="w-5 h-5 text-[#64FFDA]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8892B0]">今日借阅</p>
                    <p className="text-xl font-bold text-[#E6F1FF]">{dashboardStats.todayBorrows}</p>
                  </div>
                </div>
                <Link href="/borrow">
                  <Button variant="ghost" size="sm" className="text-[#64FFDA] hover:text-[#64FFDA] hover:bg-[#64FFDA]/10">
                    查看
                  </Button>
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-[#0A192F]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/20 flex items-center justify-center">
                    <RotateCcw className="w-5 h-5 text-[#3B82F6]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8892B0]">今日归还</p>
                    <p className="text-xl font-bold text-[#E6F1FF]">{dashboardStats.todayReturns}</p>
                  </div>
                </div>
                <Link href="/borrow">
                  <Button variant="ghost" size="sm" className="text-[#3B82F6] hover:text-[#3B82F6] hover:bg-[#3B82F6]/10">
                    查看
                  </Button>
                </Link>
              </div>

              <div className="flex items-center justify-between p-4 rounded-lg bg-[#0A192F]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#8892B0]">即将到期</p>
                    <p className="text-xl font-bold text-[#E6F1FF]">7</p>
                  </div>
                </div>
                <Link href="/borrow">
                  <Button variant="ghost" size="sm" className="text-[#F59E0B] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10">
                    查看
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Borrows & Overdue */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-[#112240] border-[#1d3557]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#E6F1FF]">最近借阅</CardTitle>
              <Link href="/borrow">
                <Button variant="ghost" size="sm" className="text-[#64FFDA]">
                  查看全部
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentBorrows.map((record) => (
                <BorrowRecordCard 
                  key={record.id} 
                  record={record}
                  onReturn={() => {}}
                  onRenew={() => {}}
                />
              ))}
            </CardContent>
          </Card>

          <Card className="bg-[#112240] border-[#1d3557]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#E6F1FF] text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                逾期提醒
              </CardTitle>
              <Link href="/borrow">
                <Button variant="ghost" size="sm" className="text-[#EF4444]">
                  处理全部
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="space-y-3">
              {overdueRecords.length > 0 ? (
                overdueRecords.map((record) => (
                  <BorrowRecordCard 
                    key={record.id} 
                    record={record}
                    onReturn={() => {}}
                    onRenew={() => {}}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-[#8892B0]">
                  <p>暂无逾期记录</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
